"use client";

import React from "react";
import { useEvents, type ChannelEventHandler } from "./pusher";
import { store } from "@store/index";
import { updateUnreadMessageCount } from "@store/features/account/workplaceSlice";
import {
  receiveMessage,
  type ListMessage,
} from "@store/features/chat/chatSlice";
import type { GetContactOutputType } from "skedyul-core/features/conversations";
import { type RouterOutputs } from "@utils/api";
import { getChatWindowState } from "./chatWindow";

interface Props {
  children: React.ReactNode;
}

const WorkplaceProvider: React.FC<Props> = ({ children }) => {
  useEvents(
    React.useMemo(() => {
      const onMessageCreated = (incoming: unknown) => {
        try {
          const env =
            typeof incoming === "object" && incoming !== null
              ? (incoming as Record<string, unknown>)
              : undefined;
          const contactPayload = (env && env["payload"]) || incoming;

          const { contactId } = getChatWindowState();
          const parsed = (contactPayload ||
            {}) as RouterOutputs["chat"]["getContact"];
          const isActive = Boolean(parsed?.id && parsed.id === contactId);
          const last = (parsed?.messages || [])[0];
          if (!last) return;
          void store.dispatch(
            receiveMessage({
              contact: parsed as unknown as GetContactOutputType,
              message: last as unknown as ListMessage,
              verified: true,
              member: undefined,
            }),
          );
          if (!isActive && !last?.memberId) {
            store.dispatch(updateUnreadMessageCount({ count: 1 }));
          }
        } catch {
          // ignore consumer errors
        }
      };

      const onMessageCreatedHandler: ChannelEventHandler = (data) => {
        onMessageCreated(data as RouterOutputs["chat"]["getContact"]);
      };

      const onWorkflowUpdated: ChannelEventHandler = (data) => {
        try {
          window.dispatchEvent(
            new CustomEvent("workflow.updated", { detail: data }),
          );
        } catch {
          // ignore broadcast errors
        }
      };

      return [
        {
          channel: ({ subdomain }: { subdomain?: string | null }) =>
            subdomain ? `private-${subdomain}` : "",
          events: {
            "message.created": onMessageCreatedHandler,
            "workflow.updated": onWorkflowUpdated,
          },
        },
      ];
    }, []),
  );

  return <>{children}</>;
};

export default WorkplaceProvider;
